import moment from 'moment-timezone';
import React from 'react';

import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Columns2 from 'components/Columns2';
import Row from 'components/Row';
import Text from 'components/Text';
import { coachingHomeEventPrefix } from 'coaching/components/DeprecatedCoachingHomeView/CoachingHomeView';
// @ts-ignore
import userAgent from 'lib/currentUserAgent';
import CoachingHomeFreeTrialCard from 'coaching/components/DeprecatedCoachingHomeView/CoachingHomeFreeTrialCard';
import { constructJoinSessionButton } from 'coaching/components/DeprecatedCoachingHomeView/CoachingSessionUpcomingInstanceRows';
import {
  ParticipantMetadata,
  CoachingSessionInstance,
  CoachingSessionTools,
} from 'coaching/components/DeprecatedCoachingHomeView/CoachingSessionsBySubjectContainer';

export type Props = {
  instance: CoachingSessionInstance;
  participantMetadata: ParticipantMetadata;
  userUuid: string;
  hasStudentInvitation: boolean;
  sessionTools: CoachingSessionTools;
};

export default function CoachingHomeFreeTrial(props: Props) {
  const { participantMetadata, userUuid, hasStudentInvitation, instance, sessionTools } = props;
  const isParentWithStudent = userIsParentWithStudent(
    participantMetadata,
    userUuid,
    hasStudentInvitation
  );
  const messageCoachTitleCopy = isParentWithStudent
    ? 'Help your child send an example homework assignment.'
    : 'Send an example homework assignment';
  const messageCoachBodyCopy = `${
    isParentWithStudent ? '' : 'as a Remind message.'
  } You can share a file, a screenshot, or even a photo of printed materials.`;
  const joinSessionBodyCopy = `${
    isParentWithStudent ? 'Help your child log in to their' : 'Log into your'
  } account at remind.com from a computer and have a large touch screen for the whiteboard.`;
  const shareHomeworkImage = userAgent.isDesktop()
    ? require('assets/images/coaching/home/share_homework_vert.png')
    : require('assets/images/coaching/home/share_homework_horiz.png');
  const prepareForSessionImage = userAgent.isDesktop()
    ? require('assets/images/coaching/home/prepare_for_session_vert.png')
    : require('assets/images/coaching/home/prepare_for_session_horiz.png');

  return (
    <BoxBody size="small">
      <Row size="small">
        <React.Fragment>
          <div data-test="next-session-element">
            <Text weight="semibold" size="medium">
              {`${constructNextSessionString(instance)} • `}
            </Text>
            <Text
              className="CoachingSessionsBySubjectContainer-duration"
              size="medium">{`${instance.duration} min`}</Text>
          </div>
          <Text element="div" size="small">
            with Coach {instance.coachName.split(' ')[0]}
          </Text>
        </React.Fragment>
      </Row>
      <Columns2
        className="CoachingSessionsBySubjectContainer-cardContainer"
        data-test="free-trial-cards"
        gutter="large">
        <CoachingHomeFreeTrialCard
          data-test="message-coach-free-trial-card"
          button={constructMessageCoachButton(sessionTools)}
          timeText="24 hours in advance"
          titleText={messageCoachTitleCopy}
          bodyText={messageCoachBodyCopy}
          image={shareHomeworkImage}
        />
        <CoachingHomeFreeTrialCard
          data-test="join-session-free-trial-card"
          button={constructJoinSessionButton(
            instance,
            sessionTools,
            'mascot',
            'xxsmall',
            'primary'
          )}
          timeText="15 minutes in advance"
          titleText="Two devices is best:"
          bodyText={joinSessionBodyCopy}
          image={prepareForSessionImage}
        />
      </Columns2>
    </BoxBody>
  );
}

function constructMessageCoachButton(sessionTools: CoachingSessionTools) {
  const { streamUuid } = sessionTools;
  const linkProps = userAgent.isDesktop()
    ? { to: `/coaching/messages/${streamUuid}` }
    : { href: `rmd://chat/${streamUuid}`, target: '_blank' };

  return (
    <Button
      {...linkProps}
      level="secondary"
      size="xxsmall"
      eventKey={`${coachingHomeEventPrefix}.message_coach.click`}>
      Message coach
    </Button>
  );
}

function userIsParentWithStudent(
  participantMetadata: ParticipantMetadata,
  userUuid: string,
  hasStudentInvitation: boolean
) {
  const isStudent = participantMetadata.studentUuid === userUuid;
  const hasChild = participantMetadata.studentUuid || hasStudentInvitation;
  return !isStudent && hasChild;
}

/**
 * Format like Thu, Nov 19 at 7:00PM (PDT)
 */
function constructNextSessionString(instance: CoachingSessionInstance) {
  const nextSession = moment(instance.startDateTime).tz(moment.tz.guess());

  return nextSession.format('ddd[,] MMM DD [at] LT [(]z[)]');
}
